import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import ApiIconButton from '../../UIControls/api-button/api-icon-button';
import ErrorDialog from '../../UIControls/api-button/ErrorDialog';
import CustomToolbarButton from './toolbar/custom-toolbar-button';
import ASWhatsAppIcon from '../../../assets/icons/Whatsapp.svg';
import ASShareIcon from '../../../assets/icons/Share.svg';
import mixpanel from 'mixpanel-browser';
import PatientDetailsDialog from './patient-details-dialog';
import { notifyAuthExpired, isAuthExpiredDialogVisible, isAuthExpiredError } from '../../../utils/auth-error-utils';

const ShareButton = ({
    generateLink,
    currentUser,
    bundledHandoutsView = false,
    accountType,
    onUpgradeRequired,
    shouldShowPatientDialog,
}) => {
    const theme = useTheme();
    // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Detect mobile devices using the user agent - it works better for me, but leaving the line above, to easy revert if does not work
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Patient details dialog state
    const [patientDialogOpen, setPatientDialogOpen] = useState(false);
    const [pendingPatientDetails, setPendingPatientDetails] = useState(null);
    const [shareErrorDialogOpen, setShareErrorDialogOpen] = useState(false);
    const [shareErrorDialogTitle, setShareErrorDialogTitle] = useState('Share error');
    const [shareErrorDialogMessage, setShareErrorDialogMessage] = useState(
        'There was an error sharing. Please try again.',
    );
    const [shareShowReloadAction, setShareShowReloadAction] = useState(false);

    const executeShare = async (patientDetails = null) => {
        try {
            const { title, url, message } = await generateLink(patientDetails);
            console.warn('share message', message);

            if (isMobile && navigator.share) {
                // native share dialog on mobile
                await navigator.share({
                    title: title,
                    text: message,
                });
            } else {
                // WhatsApp on desktop
                const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
            }

            mixpanel.track('HandoutShared', {
                email: currentUser.attributes.email,
                shareMethod: isMobile ? 'Native Share' : 'WhatsApp',
                handoutTitle: title.replace('Information Sheet: ', ''),
            });
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Share action was canceled by the user');
            } else {
                console.error('Error during share', error);
                throw error;
            }
        }
    };

    const openShareErrorDialog = (error) => {
        const isAuthExpired = isAuthExpiredError(error);
        if (isAuthExpired) {
            notifyAuthExpired({ source: 'share' });
            return;
        }
        setShareErrorDialogTitle('Share error');
        setShareErrorDialogMessage('There was an error sharing. Please try again.');
        setShareShowReloadAction(false);
        setShareErrorDialogOpen(true);
    };

    const handlePatientDetailsSubmit = async (patientDetails) => {
        // Save patient details and proceed to share
        setPendingPatientDetails(patientDetails);
        setPatientDialogOpen(false);

        // Execute share with patient details
        if (isAuthExpiredDialogVisible()) {
            return;
        }
        try {
            await executeShare(patientDetails);
        } catch (error) {
            console.error('Error during share', error);
            openShareErrorDialog(error);
        }

        // Clear pending patient details after successful share
        setPendingPatientDetails(null);
    };

    const handleShare = async () => {
        if (isAuthExpiredDialogVisible()) {
            const authDialogError = new Error('AUTH_EXPIRED_DIALOG_OPEN');
            authDialogError.isExpected = true;
            throw authDialogError;
        }

        if (accountType === 'Free') {
            onUpgradeRequired();
            return;
        }

        if (accountType === 'Student') {
            onUpgradeRequired();
            return;
        }

        // Check if we should show patient dialog first (for "My Library" content)
        if (shouldShowPatientDialog && shouldShowPatientDialog()) {
            setPatientDialogOpen(true);
        } else {
            // For "My Clinic" content, proceed directly to share
            await executeShare();
        }
    };

    const iconSrc = isMobile ? ASShareIcon : ASWhatsAppIcon;

    return (
        <>
            <PatientDetailsDialog
                open={patientDialogOpen}
                onClose={() => {
                    setPatientDialogOpen(false);
                    setPendingPatientDetails(null);
                }}
                onSubmit={handlePatientDetailsSubmit}
            />
            <ErrorDialog
                open={shareErrorDialogOpen}
                handleClose={() => {
                    setShareErrorDialogOpen(false);
                    setShareShowReloadAction(false);
                }}
                title={shareErrorDialogTitle}
                message={shareErrorDialogMessage}
                primaryActionText={shareShowReloadAction ? 'Reload Page' : null}
                onPrimaryAction={shareShowReloadAction ? () => window.location.reload() : null}
            />
            <ApiIconButton
                apiCall={handleShare}
                icon={
                    <CustomToolbarButton
                        iconSrc={iconSrc}
                        tooltipTitle={bundledHandoutsView ? 'Share handouts' : 'Share handout'}
                    />
                }
                buttonStyle={{ color: theme.palette.customColors.iconColor }}
                errorTitle={'Share error'}
                errorMessage="There was an error sharing. Please try again."
                onSuccess={() => {}}
                // successTitle={isMobile ? "Share Successful" : "WhatsApp Share Successful"}
                // successMessage="The link was shared successfully."
                ariaLabel={bundledHandoutsView ? 'Share handouts' : 'Share handout'}
                showSuccessDialog={false}
            />
        </>
    );
};

export default ShareButton;
